import {useId} from 'react'
import {FormControl, InputLabel, Select, useTheme} from '@mui/material';
import {radii} from '../theme/tokens';

/**
 * The house filled select — v3 field surface matched to {@link BaseInput}: field fill + control
 * radius, underline hairline on `fieldLine`, focus underline on the primary `blue`, all from
 * theme.tokens. Options are `<MenuItem>` children.
 *
 * Two defects were fixed when this primitive gained its first real consumer
 * (kitecorp/transferify-api-service#403); both were unreachable while nothing rendered options
 * through it, and both made the component unusable rather than merely imperfect:
 *
 * - `children` were rendered as a SIBLING of `<Select/>` (which was self-closing), so every
 *   `<MenuItem>` mounted outside the menu and MUI threw "MenuListContext is missing. MenuItems must
 *   be placed within Menu or MenuList", taking down the whole route.
 * - the label id and the field id were both the LABEL TEXT. `aria-labelledby` is a SPACE-SEPARATED
 *   id list, so a translated label ("What is wrong?") was parsed as three ids and named the field
 *   after none of them — and `labelId` pointed at the Select's own id rather than the label's, so
 *   even a single-word label named it after itself.
 */
export function BaseSelect({label, id, onChange, error, required = false, shrink, children, ...others}: any) {
	const {tokens} = useTheme();
	// Generated rather than derived from the label: an id must be a valid, space-free token in every
	// locale, and only the caller knows whether it needs a stable one (which it may pass as `id`).
	const generatedId = useId();
	const fieldId = id ?? generatedId;
	const labelId = `${fieldId}-label`;
	return (
		<FormControl fullWidth variant="filled" required={required} error={error} style={others.style}>
			{/* `shrink` is destructured out rather than spread, because it belongs to the LABEL and is not
			    a `Select` prop. It exists for the `displayEmpty` pairing: MUI decides the floating-label
			    state from the VALUE (`InputBase`'s `isFilled` is false for `""`), not from what is
			    displayed, so a select that renders a placeholder row at the empty value draws its
			    unshrunk label straight over that text. MUI's documented answer is to pass both, and
			    without this passthrough a caller could pass only half of it. */}
			<InputLabel id={labelId} htmlFor={fieldId} shrink={shrink}>{label}</InputLabel>
			<Select
				labelId={labelId}
				id={fieldId}
				label={label}
				style={{backgroundColor: tokens.field, borderTopLeftRadius: radii.control, borderTopRightRadius: radii.control}}
				sx={{
					'&:hover': {backgroundColor: tokens.field},
					'&.Mui-focused': {backgroundColor: tokens.field},
					'&:before': {borderBottomColor: tokens.fieldLine},
					'&:hover:not(.Mui-disabled):before': {borderBottomColor: tokens.fieldLine},
					'&:after': {borderBottomColor: tokens.focusOutline},
				}}
				onChange={onChange}
				{...others}
			>
				{children}
			</Select>
		</FormControl>
	);
};
